"use client"

import clsx from "clsx"
import { endOfDay, endOfMonth, endOfWeek, format, isSameDay, type Locale, startOfDay, startOfMonth, startOfWeek, subDays, subMonths } from "date-fns"
import { useEffect, useMemo, useState } from "react"
import type { DateRange } from "react-day-picker"
import { enUS, ja, ko } from "react-day-picker/locale"
import { TButton } from "../button/TButton"
import { TTextButton } from "../button/TTextButton"
import { TDivider } from "../display/TDivider"
import { TIcon } from "../display/TIcon"
import { TTag } from "../display/TTag"
import { Calendar } from "../shadcn/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "../shadcn/popover"

const getLocale = (lang: "ko" | "ja" | "en" = "ko"): Locale => {
	switch (lang) {
		case "ko":
			return ko
		case "ja":
			return ja
		case "en":
			return enUS
		default:
			return ko
	}
}

const formatDateRange = (range: DateRange | undefined, placeholder: string, locale: Locale): string => {
	if (!range?.from) {
		return placeholder
	}
	if (!range.to) {
		return `${format(range.from, "yyyy.MM.dd", { locale })} - ...`
	}
	return `${format(range.from, "yyyy.MM.dd", { locale })} - ${format(range.to, "yyyy.MM.dd", { locale })}`
}

interface DateRangeShortcut {
	label: string
	getRange: (locale: Locale) => DateRange | undefined
}

const shortcuts = (locale: Locale): DateRangeShortcut[] => [
	{ label: "All Time", getRange: () => undefined }, // 전체기간
	{ label: "Today", getRange: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }) }, // 오늘
	{ label: "Yesterday", getRange: () => ({ from: startOfDay(subDays(new Date(), 1)), to: endOfDay(subDays(new Date(), 1)) }) }, // 어제
	{ label: "This Week", getRange: () => ({ from: startOfWeek(new Date(), { locale }), to: endOfWeek(new Date(), { locale }) }) }, // 이번주
	{ label: "Last Week", getRange: () => ({ from: startOfWeek(subDays(new Date(), 7), { locale }), to: endOfWeek(subDays(new Date(), 7), { locale }) }) }, // 지난주
	{ label: "This Month", getRange: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }) }, // 이번달
	{ label: "Last Month", getRange: () => ({ from: startOfMonth(subMonths(new Date(), 1)), to: endOfMonth(subMonths(new Date(), 1)) }) }, // 지난달
	{ label: "Last 7 Days", getRange: () => ({ from: startOfDay(subDays(new Date(), 6)), to: endOfDay(new Date()) }) }, // 최근 7일
	{ label: "Last 1 Month", getRange: () => ({ from: startOfDay(subMonths(new Date(), 1)), to: endOfDay(new Date()) }) }, // 최근 1개월
	{ label: "Last 3 Months", getRange: () => ({ from: startOfDay(subMonths(new Date(), 3)), to: endOfDay(new Date()) }) } // 최근 3개월
]

const isSameRange = (range1: DateRange | undefined, range2: DateRange | undefined): boolean => {
	if (range1?.from === undefined && range1?.to === undefined && range2?.from === undefined && range2?.to === undefined) return true
	if (!range1 || !range2) return false
	if (range1.from === undefined || range1.to === undefined || range2.from === undefined || range2.to === undefined) return false

	return isSameDay(range1.from, range2.from) && isSameDay(range1.to, range2.to)
}

const getMatchingShortcutLabel = (range: DateRange | undefined, currentLocale: Locale): string | null => {
	const activeShortcut = shortcuts(currentLocale).find((shortcut) => isSameRange(shortcut.getRange(currentLocale), range))
	return activeShortcut ? activeShortcut.label : null
}

export type TDateRange = DateRange

/**
 * @description 날짜 범위 선택. 시작일~종료일을 선택하는 캘린더
 * @example
 * ```tsx
 * <TDateRangePicker
 *   value={{ from: startDate, to: endDate }}
 *   numberOfMonths={2}
 *   onChange={setDateRange}
 * />
 * ```
 */
interface TDateRangePickerProps {
	/** 선택된 날짜 범위. { from?: Date, to?: Date } */
	value?: DateRange
	/**
	 * 입력 필드 크기
	 * @default "medium"
	 * @ai-style "small" - 높이 32px
	 * @ai-style "medium" - 높이 40px
	 */
	size?: "small" | "medium"
	/** 비활성화 여부 */
	disabled?: boolean
	/** 에러 상태 표시 */
	error?: boolean
	/** 과거 날짜 선택 불가 */
	disablePast?: boolean
	/** 미래 날짜 선택 불가 */
	disableFuture?: boolean
	/**
	 * 캘린더에 표시할 달 수
	 * @default 1
	 */
	numberOfMonths?: number
	/**
	 * 캘린더 로케일
	 * @default "ko"
	 */
	currentLang?: "ko" | "ja" | "en"
	/** 추가 CSS 클래스 */
	className?: string
	/** 팝업 가로 정렬 */
	align?: "start" | "center" | "end"
	/** 팝업 표시 방향 */
	side?: "top" | "right" | "bottom" | "left"
	/** 팝업 가로 정렬 오프셋 (px) */
	alignOffset?: number
	/** 팝업 방향 오프셋 (px) */
	sideOffset?: number
	/** 날짜 미선택 시 표시할 텍스트 */
	placeholder?: string
	/** 날짜 범위 변경 시 콜백. undefined는 전체 기간 */
	onChange?: (range: DateRange | undefined) => void
}

export const TDateRangePicker = (props: TDateRangePickerProps) => {
	const {
		value: initialValue,
		size = "medium",
		disabled,
		error,
		disablePast,
		disableFuture,
		numberOfMonths = 1,
		currentLang = "ko",
		className,
		align = "start",
		side = "bottom",
		alignOffset = 0,
		sideOffset = 4,
		placeholder = "기간 선택",
		onChange
	} = props

	const [open, setOpen] = useState<boolean>(false)
	const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(initialValue)
	const [displayValue, setDisplayValue] = useState<DateRange | undefined>(initialValue)

	const currentLocale = useMemo(() => getLocale(currentLang), [currentLang])

	useEffect(() => {
		setSelectedRange(initialValue)
		setDisplayValue(initialValue)
	}, [initialValue])

	const handleShortcutClick = (shortcut: DateRangeShortcut) => {
		setSelectedRange(shortcut.getRange(currentLocale))
	}

	const handleOpenChange = (isOpen: boolean) => {
		setOpen(isOpen)
		if (!isOpen) {
			setSelectedRange(initialValue)
			setDisplayValue(initialValue)
		}
	}

	const handleReset = () => {
		setSelectedRange(initialValue)
		setDisplayValue(initialValue)
	}

	const handleCancel = () => {
		setSelectedRange(initialValue)
		setDisplayValue(initialValue)
		setOpen(false)
	}

	const handleApply = () => {
		onChange?.(selectedRange)
		setDisplayValue(selectedRange)
		setOpen(false)
	}

	const shortcutLabel = useMemo(() => getMatchingShortcutLabel(displayValue, currentLocale), [displayValue, currentLocale])

	const renderShortcuts = () => (
		<div className="flex flex-col space-y-1.5">
			{shortcuts(currentLocale).map((shortcut) => (
				<TButton
					key={shortcut.label}
					color={isSameRange(selectedRange, shortcut.getRange(currentLocale)) ? "primary" : "tertiary"}
					size="xsmall"
					className="rounded-full! font-medium! w-[100px]"
					onClick={() => handleShortcutClick(shortcut)}
				>
					{shortcut.label}
				</TButton>
			))}
		</div>
	)

	const renderFooter = () => (
		<footer className="flex justify-between items-center">
			<TTextButton size="small" color="secondary" onClick={handleReset}>
				Reset
			</TTextButton>
			<div className="flex items-center space-x-2">
				<TButton size="small" color="line" onClick={handleCancel}>
					Cancel
				</TButton>
				<TButton size="small" color="primary" onClick={handleApply}>
					Apply
				</TButton>
			</div>
		</footer>
	)

	return (
		<Popover onOpenChange={handleOpenChange} open={open}>
			<PopoverTrigger asChild disabled={disabled}>
				<div className={clsx("TDate range-picker", size, className)} data-open={open} data-disabled={disabled} data-error={error}>
					{shortcutLabel && (
						<div className="ml-2 mr-1">
							<TTag size="small">{shortcutLabel}</TTag>
						</div>
					)}
					<label
						className={clsx(
							"label",
							shortcutLabel ? "pl-1" : "pl-3",
							(!displayValue?.from || (displayValue.from === undefined && displayValue.to === undefined)) && "text-text-placeholder"
						)}
					>
						{formatDateRange(displayValue, placeholder, currentLocale)}
					</label>
					<div className="ml-2 mr-3">
						<TIcon size={16} name="calendar" />
					</div>
				</div>
			</PopoverTrigger>
			<PopoverContent align={align} side={side} alignOffset={alignOffset} sideOffset={sideOffset} className="space-y-4">
				<div className="flex space-x-4 items-end">
					<Calendar
						mode="range"
						locale={currentLocale}
						defaultMonth={selectedRange?.from || initialValue?.from || new Date()}
						selected={selectedRange}
						onSelect={setSelectedRange}
						numberOfMonths={numberOfMonths}
						disabled={(date: Date) => {
							if (disablePast && date < startOfDay(new Date())) {
								return true
							}
							if (disableFuture && date > endOfDay(new Date())) {
								return true
							}
							return false
						}}
						formatters={{
							formatWeekdayName: (date) => format(date, "EEE", { locale: getLocale(currentLang) }),
							formatCaption: (date) =>
								currentLang === "ko"
									? format(date, "yyyy년 M월", { locale: ko })
									: currentLang === "ja"
										? format(date, "yyyy年M月", { locale: ja })
										: format(date, "LLLL yyyy", { locale: enUS })
						}}
					/>
					{renderShortcuts()}
				</div>
				<TDivider />
				{renderFooter()}
			</PopoverContent>
		</Popover>
	)
}

TDateRangePicker.displayName = "TDateRangePicker"
